import { Component } from '@theme/component';
import { fetchConfig, prefersReducedMotion } from '@theme/utilities';
import { CartErrorEvent, CartUpdateEvent } from '@theme/events';

class GiftWrapOrder extends Component {
  refs = {};
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.refs.toggle?.addEventListener('change', this.#handleToggle, { signal });
    this.refs.saveButton?.addEventListener('click', this.#handleSave, { signal });
    this.refs.editButton?.addEventListener('click', this.#handleEdit, { signal });
    this.refs.fromInput?.addEventListener('input', this.#clearValidation, { signal });
    this.refs.toInput?.addEventListener('input', this.#clearValidation, { signal });
    this.refs.messageInput?.addEventListener('input', this.#handleMessageInput, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #handleToggle = async () => {
    if (!(this.refs.toggle instanceof HTMLInputElement)) return;

    const shouldAdd = this.refs.toggle.checked;

    this.#clearError();

    if (shouldAdd) {
      if (this.refs.messageSummary instanceof HTMLElement) this.refs.messageSummary.classList.add('hidden');
      if (this.refs.messageForm instanceof HTMLElement) this.refs.messageForm.classList.remove('hidden');
      await this.#scrollToMessageForm();
      return;
    }

    if (!this.#hasGiftWrap) {
      this.#clearFields();
      if (this.refs.messageSummary instanceof HTMLElement) this.refs.messageSummary.classList.add('hidden');
      if (this.refs.messageForm instanceof HTMLElement) {
        this.refs.messageForm.classList.add('hidden');
      }
      return;
    }

    this.#setBusy(true);

    try {
      const result = await this.#removeGiftWrap();
      this.#dispatchCartUpdate(result);
    } catch (error) {
      this.refs.toggle.checked = true;
      this.#showError(error);
    } finally {
      this.#setBusy(false);
    }
  };

  #handleSave = async () => {
    if (!this.#validate()) return;

    const lineKey = this.dataset.giftWrapLineKey || '';
    this.#setBusy(true);

    try {
      const result = lineKey ? await this.#updateGiftWrap(lineKey) : await this.#addGiftWrap();

      const fromValue = this.#fromValue;
      const toValue = this.#toValue;
      const messageValue = this.#messageValue;

      this.#dispatchCartUpdate(result);

      if (this.refs.summaryFrom instanceof HTMLElement) this.refs.summaryFrom.textContent = fromValue;
      if (this.refs.summaryTo instanceof HTMLElement) this.refs.summaryTo.textContent = toValue;
      if (this.refs.summaryMessage instanceof HTMLElement) this.refs.summaryMessage.textContent = messageValue;

      if (this.refs.messageForm instanceof HTMLElement) this.refs.messageForm.classList.add('hidden');
      if (this.refs.messageSummary instanceof HTMLElement) this.refs.messageSummary.classList.remove('hidden');

      window.scrollTo({ top: 0, behavior: prefersReducedMotion() ? 'instant' : 'smooth' });
    } catch (error) {
      this.#showError(error);
    } finally {
      this.#setBusy(false);
    }
  };

  #handleMessageInput = () => {
    if (!(this.refs.messageInput instanceof HTMLTextAreaElement)) return;

    this.refs.messageInput.value = this.refs.messageInput.value.slice(0, 250);

    if (this.refs.messageCount instanceof HTMLElement) {
      this.refs.messageCount.textContent = String(this.refs.messageInput.value.length);
    }
  };

  #handleEdit = () => {
    if (this.refs.messageSummary instanceof HTMLElement) this.refs.messageSummary.classList.add('hidden');
    if (this.refs.messageForm instanceof HTMLElement) {
      this.refs.messageForm.classList.remove('hidden');
      this.refs.messageForm.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    }
  };

  #clearValidation = () => {
    if (this.refs.fromError instanceof HTMLElement && this.#fromValue) {
      this.refs.fromError.classList.add('hidden');
    }

    if (this.refs.toError instanceof HTMLElement && this.#toValue) {
      this.refs.toError.classList.add('hidden');
    }
  };

  async #updateGiftWrap(lineKey) {
    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
      body: JSON.stringify({
        id: lineKey,
        quantity: Number(this.dataset.giftWrapQuantity || 1),
        properties: this.#properties,
        sections: this.#getSections(),
        sections_url: window.location.pathname + window.location.search,
      }),
    }));
    const result = await response.json();

    if (!response.ok || result.errors) {
      throw new Error(result.errors || result.description || 'Unable to save the gift wrap message.');
    }

    return result;
  }

  async #addGiftWrap() {
    const variantId = Number(this.dataset.giftWrapVariantId || 0);
    if (!variantId) throw new Error('Gift wrap is currently unavailable.');

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', {
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: this.#properties,
          },
        ],
        sections: this.#getSections(),
        sections_url: window.location.pathname + window.location.search,
      }),
    }));
    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.message || result.description || 'Unable to add gift wrap.');
    }

    return result;
  }

  async #removeGiftWrap() {
    const lineKeys = new Set();

    document.querySelectorAll('[data-gift-wrap-item="true"][data-line-key]').forEach((row) => {
      if (row instanceof HTMLElement && row.dataset.lineKey) {
        lineKeys.add(row.dataset.lineKey);
      }
    });

    if (this.dataset.giftWrapLineKey) {
      lineKeys.add(this.dataset.giftWrapLineKey);
    }

    if (!lineKeys.size) throw new Error('Unable to find the gift wrap in your bag.');

    const updates = Object.fromEntries(Array.from(lineKeys, (lineKey) => [lineKey, 0]));
    const response = await fetch(Theme.routes.cart_update_url, fetchConfig('json', {
      body: JSON.stringify({
        updates,
        sections: this.#getSections(),
        sections_url: window.location.pathname + window.location.search,
      }),
    }));
    const result = await response.json();

    if (!response.ok || result.errors) {
      throw new Error(result.errors || result.description || 'Unable to remove gift wrap.');
    }

    return result;
  }

  #validate() {
    const hasFrom = this.#fromValue !== '';
    const hasTo = this.#toValue !== '';

    if (this.refs.fromError instanceof HTMLElement) {
      this.refs.fromError.classList.toggle('hidden', hasFrom);
    }

    if (this.refs.toError instanceof HTMLElement) {
      this.refs.toError.classList.toggle('hidden', hasTo);
    }

    return hasFrom && hasTo;
  }

  #dispatchCartUpdate(result) {
    const sections = result.sections || {};
    const firstSectionId = Object.keys(sections)[0];
    let itemCount = 0;

    if (firstSectionId && sections[firstSectionId]) {
      const html = new DOMParser().parseFromString(sections[firstSectionId], 'text/html');
      const hiddenCount = html.querySelector('[ref="cartItemCount"]')?.textContent;
      itemCount = hiddenCount ? parseInt(hiddenCount, 10) : 0;
    }

    document.dispatchEvent(new CartUpdateEvent(result, 'gift-wrap-order', {
      source: 'gift-wrap-order',
      itemCount,
      sections,
      preventCartDrawer: true,
    }));
  }

  async #scrollToMessageForm() {
    const productId = this.dataset.giftWrapProductId;
    const messageForm = await this.#waitForMessageForm(productId);

    if (!(messageForm instanceof HTMLElement)) return;

    await new Promise((resolve) => setTimeout(resolve, 150));

    const startY = window.scrollY;
    const initialTargetY = startY + messageForm.getBoundingClientRect().top - 32;

    if (initialTargetY <= startY) return;

    if (prefersReducedMotion()) {
      window.scrollTo(0, initialTargetY);
      return;
    }

    const duration = 800;
    const startedAt = performance.now();

    await new Promise((resolve) => {
      const animate = (now) => {
        const progress = Math.min((now - startedAt) / duration, 1);
        const easedProgress =
          progress < 0.5
            ? 4 * progress * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 3) / 2;
        const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
        const targetY = Math.min(
          window.scrollY + messageForm.getBoundingClientRect().top - 32,
          maxScrollY
        );

        window.scrollTo(0, startY + (targetY - startY) * easedProgress);

        if (progress < 1) {
          window.requestAnimationFrame(animate);
        } else {
          resolve();
        }
      };

      window.requestAnimationFrame(animate);
    });
  }

  #waitForMessageForm(productId) {
    const findMessageForm = () => {
      const giftWrapOrders = document.querySelectorAll('gift-wrap-order');
      const giftWrapOrder = Array.from(giftWrapOrders).find(
        (element) => element instanceof HTMLElement && element.dataset.giftWrapProductId === productId
      );
      const messageForm = giftWrapOrder?.querySelector('[ref="messageForm"]:not(.hidden)');

      return messageForm instanceof HTMLElement ? messageForm : null;
    };

    const existingForm = findMessageForm();
    if (existingForm) return Promise.resolve(existingForm);

    return new Promise((resolve) => {
      const observer = new MutationObserver(() => {
        const messageForm = findMessageForm();
        if (!messageForm) return;

        observer.disconnect();
        clearTimeout(timeout);
        resolve(messageForm);
      });
      const timeout = setTimeout(() => {
        observer.disconnect();
        resolve(null);
      }, 1500);

      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
        childList: true,
        subtree: true,
      });
    });
  }

  #showError(error) {
    const message = error instanceof Error ? error.message : 'Unable to update gift wrap.';

    if (this.refs.formError instanceof HTMLElement) {
      this.refs.formError.textContent = message;
      this.refs.formError.classList.remove('hidden');
    }

    document.dispatchEvent(new CartErrorEvent('gift-wrap-order', message));
  }

  #clearError() {
    if (!(this.refs.formError instanceof HTMLElement)) return;

    this.refs.formError.textContent = '';
    this.refs.formError.classList.add('hidden');
  }

  #clearFields() {
    if (this.refs.fromInput instanceof HTMLInputElement) this.refs.fromInput.value = '';
    if (this.refs.toInput instanceof HTMLInputElement) this.refs.toInput.value = '';
    if (this.refs.messageInput instanceof HTMLTextAreaElement) this.refs.messageInput.value = '';
    if (this.refs.messageCount instanceof HTMLElement) this.refs.messageCount.textContent = '0';

    this.refs.fromError?.classList.add('hidden');
    this.refs.toError?.classList.add('hidden');
    this.#clearError();
  }

  #setBusy(isBusy) {
    if (this.refs.toggle instanceof HTMLInputElement) {
      this.refs.toggle.disabled = isBusy;
    }

    if (this.refs.saveButton instanceof HTMLButtonElement) {
      this.refs.saveButton.disabled = isBusy;
      this.refs.saveButton.classList.toggle('atc-loading', isBusy);
    }

    this.classList.toggle('gift-wrap-order--loading', isBusy);
  }

  #getSections() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');
  }

  get #hasGiftWrap() {
    return Boolean(this.dataset.giftWrapLineKey);
  }

  get #properties() {
    return {
      _personalisation_role: 'gift_wrap',
      _gift_wrap_order: 'true',
      From: this.#fromValue,
      To: this.#toValue,
      Message: this.#messageValue,
    };
  }

  get #fromValue() {
    return this.refs.fromInput instanceof HTMLInputElement ? this.refs.fromInput.value.trim() : '';
  }

  get #toValue() {
    return this.refs.toInput instanceof HTMLInputElement ? this.refs.toInput.value.trim() : '';
  }

  get #messageValue() {
    return this.refs.messageInput instanceof HTMLTextAreaElement ? this.refs.messageInput.value.trim() : '';
  }
}

if (!customElements.get('gift-wrap-order')) {
  customElements.define('gift-wrap-order', GiftWrapOrder);
}

class GiftWrapMinicart extends Component {
  refs = {};
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.refs.toggle?.addEventListener('change', this.#handleToggle, { signal });
    this.refs.closeButton?.addEventListener('click', () => this.#closePanel(), { signal });
    this.refs.clearButton?.addEventListener('click', this.#handleClear, { signal });
    this.refs.saveButton?.addEventListener('click', this.#handleSave, { signal });
    this.refs.fromInput?.addEventListener('input', this.#clearValidation, { signal });
    this.refs.toInput?.addEventListener('input', this.#clearValidation, { signal });
    this.refs.messageInput?.addEventListener('input', this.#handleMessageInput, { signal });
    this.addEventListener('keydown', this.#handleKeyDown, { signal, capture: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #handleToggle = () => {
    if (!(this.refs.toggle instanceof HTMLInputElement)) return;

    if (this.#hasGiftWrap) {
      this.refs.toggle.checked = true;
    }

    this.#openPanel();
  };

  #handleSave = async () => {
    if (!this.#validate()) return;

    this.#setBusy(true);

    try {
      const result = this.#hasGiftWrap
        ? await this.#updateGiftWrap()
        : await this.#addGiftWrap();

      this.#closePanel(false);
      this.#dispatchCartUpdate(result);
    } catch (error) {
      this.#showError(error);
    } finally {
      this.#setBusy(false);
    }
  };

  #handleClear = async () => {
    if (!this.#hasGiftWrap) {
      this.#clearFields();
      this.refs.fromInput?.focus();
      return;
    }

    this.#setBusy(true);

    try {
      const result = await this.#removeGiftWrap();
      this.#closePanel(false);
      this.#dispatchCartUpdate(result);
    } catch (error) {
      this.#showError(error);
    } finally {
      this.#setBusy(false);
    }
  };

  #handleMessageInput = () => {
    if (!(this.refs.messageInput instanceof HTMLTextAreaElement)) return;

    this.refs.messageInput.value = this.refs.messageInput.value.slice(0, 250);

    if (this.refs.messageCount instanceof HTMLElement) {
      this.refs.messageCount.textContent = String(this.refs.messageInput.value.length);
    }
  };

  #handleKeyDown = (event) => {
    if (event.key !== 'Escape' || !this.#isOpen) return;

    event.preventDefault();
    event.stopPropagation();
    this.#closePanel();
  };

  #openPanel() {
    if (!(this.refs.panel instanceof HTMLElement)) return;

    this.#clearError();
    this.refs.panel.removeAttribute('inert');
    this.refs.panel.setAttribute('aria-hidden', 'false');
    this.refs.toggle?.setAttribute('aria-expanded', 'true');

    window.requestAnimationFrame(() => {
      this.refs.panel?.classList.add('is-open');
      this.refs.fromInput?.focus({ preventScroll: true });
    });
  }

  #closePanel = (restoreFocus = true) => {
    if (!(this.refs.panel instanceof HTMLElement)) return;

    this.refs.panel.classList.remove('is-open');
    this.refs.panel.setAttribute('aria-hidden', 'true');
    this.refs.panel.setAttribute('inert', '');
    this.refs.toggle?.setAttribute('aria-expanded', 'false');

    if (this.refs.toggle instanceof HTMLInputElement) {
      this.refs.toggle.checked = this.#hasGiftWrap;
      if (restoreFocus) this.refs.toggle.focus({ preventScroll: true });
    }
  };

  async #addGiftWrap() {
    const variantId = Number(this.dataset.giftWrapVariantId || 0);
    if (!variantId) throw new Error('Gift wrap is currently unavailable.');

    const response = await fetch(Theme.routes.cart_add_url, fetchConfig('json', {
      body: JSON.stringify({
        items: [
          {
            id: variantId,
            quantity: 1,
            properties: this.#properties,
          },
        ],
        sections: this.#getSections(),
        sections_url: window.location.pathname + window.location.search,
      }),
    }));
    const result = await response.json();

    if (!response.ok || result.status) {
      throw new Error(result.message || result.description || 'Unable to add gift wrap.');
    }

    return result;
  }

  async #updateGiftWrap() {
    const lineKey = this.dataset.giftWrapLineKey || '';
    if (!lineKey) throw new Error('Unable to find the gift wrap in your bag.');

    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
      body: JSON.stringify({
        id: lineKey,
        quantity: Number(this.dataset.giftWrapQuantity || 1),
        properties: this.#properties,
        sections: this.#getSections(),
        sections_url: window.location.pathname + window.location.search,
      }),
    }));
    const result = await response.json();

    if (!response.ok || result.errors) {
      throw new Error(result.errors || result.description || 'Unable to save gift wrap.');
    }

    return result;
  }

  async #removeGiftWrap() {
    const lineKey = this.dataset.giftWrapLineKey || '';
    if (!lineKey) throw new Error('Unable to find the gift wrap in your bag.');

    const response = await fetch(Theme.routes.cart_change_url, fetchConfig('json', {
      body: JSON.stringify({
        id: lineKey,
        quantity: 0,
        sections: this.#getSections(),
        sections_url: window.location.pathname + window.location.search,
      }),
    }));
    const result = await response.json();

    if (!response.ok || result.errors) {
      throw new Error(result.errors || result.description || 'Unable to remove gift wrap.');
    }

    return result;
  }

  #validate() {
    const hasFrom = this.#fromValue !== '';
    const hasTo = this.#toValue !== '';

    if (this.refs.fromError instanceof HTMLElement) {
      this.refs.fromError.classList.toggle('hidden', hasFrom);
    }

    if (this.refs.toError instanceof HTMLElement) {
      this.refs.toError.classList.toggle('hidden', hasTo);
    }

    return hasFrom && hasTo;
  }

  #clearValidation = () => {
    if (this.refs.fromError instanceof HTMLElement && this.#fromValue) {
      this.refs.fromError.classList.add('hidden');
    }

    if (this.refs.toError instanceof HTMLElement && this.#toValue) {
      this.refs.toError.classList.add('hidden');
    }
  };

  #clearFields() {
    if (this.refs.fromInput instanceof HTMLInputElement) this.refs.fromInput.value = '';
    if (this.refs.toInput instanceof HTMLInputElement) this.refs.toInput.value = '';
    if (this.refs.messageInput instanceof HTMLTextAreaElement) this.refs.messageInput.value = '';
    if (this.refs.messageCount instanceof HTMLElement) this.refs.messageCount.textContent = '0';

    this.refs.fromError?.classList.add('hidden');
    this.refs.toError?.classList.add('hidden');
    this.#clearError();
  }

  #dispatchCartUpdate(result) {
    const sections = result.sections || {};
    const firstSectionId = Object.keys(sections)[0];
    let itemCount = 0;

    if (firstSectionId && sections[firstSectionId]) {
      const html = new DOMParser().parseFromString(sections[firstSectionId], 'text/html');
      const hiddenCount = html.querySelector('[ref="cartItemCount"]')?.textContent;
      itemCount = hiddenCount ? parseInt(hiddenCount, 10) : 0;
    }

    document.dispatchEvent(new CartUpdateEvent(result, 'gift-wrap-minicart', {
      source: 'gift-wrap-minicart',
      itemCount,
      sections,
      preventCartDrawer: true,
    }));
  }

  #showError(error) {
    const message = error instanceof Error ? error.message : 'Unable to update gift wrap.';

    if (this.refs.formError instanceof HTMLElement) {
      this.refs.formError.textContent = message;
      this.refs.formError.classList.remove('hidden');
    }

    document.dispatchEvent(new CartErrorEvent('gift-wrap-minicart', message));
  }

  #clearError() {
    if (!(this.refs.formError instanceof HTMLElement)) return;

    this.refs.formError.textContent = '';
    this.refs.formError.classList.add('hidden');
  }

  #setBusy(isBusy) {
    for (const ref of [this.refs.toggle, this.refs.clearButton, this.refs.saveButton]) {
      if (ref instanceof HTMLInputElement || ref instanceof HTMLButtonElement) {
        ref.disabled = isBusy;
      }
    }

    if (this.refs.saveButton instanceof HTMLButtonElement) {
      this.refs.saveButton.classList.toggle('atc-loading', isBusy);
    }

    this.classList.toggle('gift-wrap-minicart--loading', isBusy);
  }

  #getSections() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => (component instanceof HTMLElement ? component.dataset.sectionId : ''))
      .filter(Boolean)
      .join(',');
  }

  get #hasGiftWrap() {
    return Boolean(this.dataset.giftWrapLineKey);
  }

  get #isOpen() {
    return this.refs.panel instanceof HTMLElement && this.refs.panel.classList.contains('is-open');
  }

  get #properties() {
    return {
      _personalisation_role: 'gift_wrap',
      _gift_wrap_order: 'true',
      From: this.#fromValue,
      To: this.#toValue,
      Message: this.#messageValue,
    };
  }

  get #fromValue() {
    return this.refs.fromInput instanceof HTMLInputElement ? this.refs.fromInput.value.trim() : '';
  }

  get #toValue() {
    return this.refs.toInput instanceof HTMLInputElement ? this.refs.toInput.value.trim() : '';
  }

  get #messageValue() {
    return this.refs.messageInput instanceof HTMLTextAreaElement ? this.refs.messageInput.value.trim() : '';
  }
}

if (!customElements.get('gift-wrap-minicart')) {
  customElements.define('gift-wrap-minicart', GiftWrapMinicart);
}
